import { useCallback, useRef, useState } from 'react';
import { ApiError } from '../api/http';

type Resource = 'modelos' | 'assuntos' | 'conversa';
type Event = { at: string; resource: Resource; status: number | null; trace: string | null; recovered: boolean };
const KEY = 'td-conversation-connection-v1';
function initial(): Event[] {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? '[]');
    return Array.isArray(value) ? value.filter(e => e && typeof e.at === 'string' && ['modelos', 'assuntos', 'conversa'].includes(e.resource)
      && (e.status === null || typeof e.status === 'number') && typeof e.recovered === 'boolean').slice(-20) as Event[] : [];
  } catch { return []; }
}
export function useConversationConnection() {
  const [failures, setFailures] = useState<Partial<Record<Resource, number | null>>>({});
  const liveFailures = useRef<Partial<Record<Resource, number | null>>>({});
  const [events, setEvents] = useState<Event[]>(initial);
  const append = useCallback((event: Event) => setEvents(previous => {
    const last = previous.at(-1);
    if (last?.resource === event.resource && last.status === event.status && last.recovered === event.recovered) return previous;
    const next = [...previous, event].slice(-20);
    try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage unavailable: keep this page's history */ }
    return next;
  }), []);
  const failure = useCallback((resource: Resource, cause: unknown) => {
    const status = cause instanceof ApiError ? cause.status : null;
    liveFailures.current = { ...liveFailures.current, [resource]: status };
    setFailures(liveFailures.current);
    append({ at: new Date().toISOString(), resource, status, trace: cause instanceof ApiError ? cause.traceId : null, recovered: false });
  }, [append]);
  const success = useCallback((resource: Resource) => {
    if (!(resource in liveFailures.current)) return;
    const next = { ...liveFailures.current }; delete next[resource];
    liveFailures.current = next; setFailures(next);
    append({ at: new Date().toISOString(), resource, status: null, trace: null, recovered: true });
  }, [append]);
  const resources = Object.keys(failures);
  return { failure, success, events, readError: resources.length ? `Não consegui atualizar ${resources.join(', ')}. A última leitura continua visível; a reconexão é automática.` : null };
}
